import React, { FC, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Swal from 'sweetalert2'
import {
  Column,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import { KTIcon } from '../../../_payairo/helpers'
import { PageLink, PageTitle, useLayout } from '../../../_payairo/layout/core'
import ModalEditUser from '../../../_payairo/partials/widgets/_new/modal/ModalEditUser'
import clsx from 'clsx'

type UserType = {
  uuid: string
  name: string
  email: string
  created_at: string
  role: string
  is_active: boolean
}

const UserManagement: FC = () => {
  const [users, setUsers] = useState<UserType[]>([])
  const [loading, setLoading] = useState(false)
  const { setSelectedUserDetails } = useLayout()

  // Example Data
  useEffect(() => {
    setLoading(true)
    setTimeout(() => {
      setUsers([
        {
          uuid: '1',
          name: 'John Doe',
          email: 'john.doe@example.com',
          created_at: '2025-09-12T10:15:00',
          role: 'Admin',
          is_active: true,
        },
        {
          uuid: '2',
          name: 'Jane Smith',
          email: 'jane.smith@example.com',
          created_at: '2025-08-22T14:45:00',
          role: 'Manager',
          is_active: false,
        },
        {
          uuid: '3',
          name: 'Ravi Kumar',
          email: 'ravi.kumar@example.com',
          created_at: '2025-10-02T09:00:00',
          role: 'Staff',
          is_active: true,
        },
      ])
      setLoading(false)
    }, 1000)
  }, [])

  // Enable/Disable user
  const toggleUserStatus = async (uuid: string, enable: boolean) => {
    const action = enable ? 'enable' : 'disable'
    const confirmText = enable
      ? 'Yes, enable this user!'
      : 'Yes, disable this user!'
    const successText = enable
      ? 'User has been enabled successfully.'
      : 'User has been disabled successfully.'
    const confirmColor = enable ? '#28a745' : '#dc3545'

    const result = await Swal.fire({
      title: `Are you sure you want to ${action} this user?`,
      text: `This action will ${enable ? 'activate' : 'deactivate'} the user's account.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: confirmColor,
      cancelButtonColor: '#6c757d',
      confirmButtonText: confirmText,
    })

    if (result.isConfirmed) {
      try {
        // 👉 Place your API call or logic here
        console.log(`${action.toUpperCase()} user: ${uuid}`)
        // Example: await api.updateUserStatus(uuid, enable)

        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: successText,
          timer: 2000,
          showConfirmButton: false,
        })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while updating the user status.',
        })
      }
    }
  }


  const columns: Column<UserType>[] = useMemo(
    () => [
      {
        Header: 'S.No',
        id: 'serial-number',
        Cell: ({ row }) => <>{row.index + 1}</>,
      },
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Email ID',
        accessor: 'email',
      },
      {
        Header: 'Role',
        accessor: 'role',
        Cell: ({ value }) => (
          <span className='badge badge-light-primary fs-7 m-1'>
            {value || 'N/A'}
          </span>
        ),
      },
      {
        Header: 'Created At',
        accessor: 'created_at',
        Cell: ({ value }) => (
          <>
            <div>{moment(value).format('DD MMM, YYYY')}</div>
            <div className='text-muted'>{moment(value).format('hh:mm A')}</div>
          </>
        ),
      },
      {
        Header: 'Action',
        Cell: ({ ...props }) => {
          const uuid = props.row.original.uuid
          const status = props.row.original.is_active

          return (
            <div className='d-flex flex-wrap gap-6' style={{ width: "200px" }}>
              <a
                id='user-details-drawer-toggle'
                href='javascript:void(0)'
                className='btn h-35px w-65px btn-icon btn-light-info'
                onClick={() => setSelectedUserDetails(uuid)}
              >
                View
              </a>

              {/* Enable button (tick) */}
              <button
                className={clsx(
                  'btn h-35px btn-icon btn btn-light-success',
                  status === true && 'disabled btn-secondary'
                )}
                disabled={status === true}
                onClick={() => toggleUserStatus(uuid, true)} // Enable user
              >
                <KTIcon
                  iconName='check'
                  className={clsx(
                    'text-hover-white fs-1',
                    status === true ? 'text-gray' : 'text-success'
                  )}
                />
              </button>

              {/* Disable button (cross) */}
              <button
                className={clsx(
                  'btn h-35px btn-icon btn btn-light-danger',
                  status === false && 'disabled btn-secondary'
                )}
                disabled={status === false}
                onClick={() => toggleUserStatus(uuid, false)}
              >
                <KTIcon
                  iconName='cross'
                  className={clsx(
                    'text-hover-white fs-1',
                    status === false ? 'text-gray' : 'text-danger'
                  )}
                />
              </button>
            </div>
          )
        },
      },

    ],
    [users]
  )

  const table = useTable(
    { columns, data: users },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  return (
    <>
      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>User Management</span>
            <span className='text-muted fw-semibold fs-7'>
              Total {users?.length} Users
            </span>
          </h3>
          <div className='d-flex align-items-center'>
            <input
              type='text'
              className='form-control form-control-sm w-200px me-3'
              placeholder='Search User'
              value={table.state.globalFilter ?? ''}
              onChange={(e) => table.setGlobalFilter(e.target.value)}
            />
          </div>
        </div>
        <div className='card-body pt-3'>
          <div className='table-responsive'>
            <table
              {...table.getTableProps()}
              className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
            >
              <thead>
                {table.headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                      >
                        {column.render('Header')}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <KTIcon iconName='down' className='ms-1 text-muted' />
                          ) : (
                            <KTIcon iconName='up' className='ms-1 text-muted' />
                          )
                        ) : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...table.getTableBodyProps()}>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className='text-center'>
                      <div className='spinner-border text-primary' role='status'>
                        <span className='visually-hidden'>Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : table.page.length > 0 ? (
                  table.page.map((row) => {
                    table.prepareRow(row)
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                        ))}
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className='text-center'>
                      <span className='fw-bold'>No data found</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination Controls */}
            <div className='d-flex justify-content-between align-items-center mt-5'>
              <div>
                <select
                  className='form-select form-select-sm form-select-solid'
                  value={table.state.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                Page <strong>{table.state.pageIndex + 1}</strong> of{' '}
                {table.pageOptions.length}
              </div>
              <div>
                <button
                  className='btn btn-sm'
                  onClick={() => table.previousPage()}
                  disabled={!table.canPreviousPage}
                >
                  <KTIcon iconName='left' className='text-gray fs-1 me-5' />
                </button>
                <button
                  className='btn btn-sm'
                  onClick={() => table.nextPage()}
                  disabled={!table.canNextPage}
                >
                  <KTIcon iconName='right' className='text-gray fs-1 me-5' />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const myBreadCrumbs: PageLink[] = [
  { title: 'Home', path: '/dashboard', isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
]

const UserManagementWrapper: FC = () => {
  const { setRightTolbar } = useLayout()

  useEffect(() => {
    setRightTolbar('usermanagement')
  }, [])

  return (
    <>
      <PageTitle breadcrumbs={myBreadCrumbs}>User Management</PageTitle>
      <UserManagement />
    </>
  )
}

export { UserManagementWrapper }
